import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Phone, Mail } from 'lucide-react'
import logoImage from '../../assets/images/C9LogoLightTheme.png'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Partners', href: '/partners' },
    { name: 'Clients', href: '/clients' },
    { name: 'Candidates', href: '/candidates' },
    { name: 'Jobs', href: '/jobs' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ]

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActive = (href) => location.pathname === href

  return (
    <header className={`bg-white sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'shadow-xl backdrop-blur-sm bg-white/95' : 'shadow-sm'
    }`}>
      {/* Blue Top Bar */}
      <div className="bg-c9-blue text-white py-2">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <a href="tel:02081524574" className="flex items-center space-x-2 hover:text-c9-orange transition-colors">
                <Phone className="h-4 w-4" />
                <span>020 8152 4574</span>
              </a>
              <a href="mailto:contact@c9-recruitment.com" className="flex items-center space-x-2 hover:text-c9-orange transition-colors">
                <Mail className="h-4 w-4" />
                <span>contact@c9-recruitment.com</span>
              </a>
            </div>
            <div className="hidden md:block">
              <span className="text-gray-200">Efficient Recruitment Solutions for Your Business</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Full Width */}
      <nav className="w-full min-h-[80px] flex items-center px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 relative">
        <div className="flex items-center justify-between w-full">
          {/* Logo - No Extra Padding */}
          <Link to="/" className="flex items-center hover:opacity-90 transition-opacity h-full -ml-2">
            <img 
              src={logoImage} 
              alt="C9 Recruitment Logo - Light Theme" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Centered Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 max-w-2xl mx-auto h-full">
            <div className="flex items-center space-x-10">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-semibold tracking-wide transition-all duration-200 hover:text-c9-orange relative group py-2 ${
                    isActive(item.href) 
                      ? 'text-c9-orange' 
                      : 'text-gray-700'
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-c9-orange transition-all duration-300 group-hover:w-full ${
                    isActive(item.href) ? 'w-full' : ''
                  }`}></span>
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side CTA - No Extra Padding */}
          <div className="hidden lg:flex items-center space-x-3 h-full -mr-2">
            <Link to="/jobs" className="text-sm font-medium text-gray-600 hover:text-c9-orange transition-colors px-3 py-2">
              View Jobs
            </Link>
            <Link to="/clients" className="btn-primary text-sm px-6 py-2.5 hover:shadow-lg hover:scale-105 transition-all duration-300">
              Hire with Us
            </Link>
          </div>

          {/* Mobile Menu Button - Properly aligned to right */}
          <div className="lg:hidden flex items-center justify-end">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? 
                <X className="h-6 w-6 text-gray-700" /> : 
                <Menu className="h-6 w-6 text-gray-700" />
              }
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Absolutely positioned full width dropdown */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 transition-all duration-300 ease-in-out transform ${
          isMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-2 opacity-0 invisible'
        }`}>
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-col space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`font-medium transition-colors duration-200 py-3 px-4 rounded-lg ${
                    isActive(item.href)
                      ? 'text-c9-orange bg-c9-orange/5'
                      : 'text-gray-700 hover:text-c9-orange hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile CTA */}
              <div className="pt-4 mt-4 border-t border-gray-100 space-y-3 px-4">
                <Link 
                  to="/jobs" 
                  className="btn-outline text-center text-sm py-2.5 block w-full"
                  onClick={() => setIsMenuOpen(false)}
                >
                  View Job Opportunities
                </Link>
                <Link 
                  to="/clients" 
                  className="btn-primary text-center text-sm py-2.5 block w-full"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Hire with Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  )
}
